/**
 * OTA Live Update Service
 * 
 * Checks Firestore for a new bundle on app launch.
 * Downloads and applies updates via @capawesome/capacitor-live-update.
 */

import { Capacitor } from '@capacitor/core';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { doc, getDocFromServer } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const OTA_CHECK_TIMEOUT_MS = 5000;
export const OTA_DOC_PATH = 'appConfig';
export const OTA_DOC_ID = 'otaConfig';

interface OtaMetadata {
  bundleId: string;
  downloadUrl: string;
}

/**
 * Race a promise against a timeout. Returns undefined if the timeout wins.
 * Clears the timer when the original promise settles to avoid timer leaks.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return new Promise<T | undefined>((resolve, reject) => {
    const timeoutHandle = setTimeout(() => resolve(undefined), ms);

    promise.then(
      (value) => {
        clearTimeout(timeoutHandle);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutHandle);
        reject(error);
      },
    );
  });
}

/**
 * Check for OTA updates and apply if a new bundle is available.
 * 
 * - Runs only on native platforms (no-op on web).
 * - Never throws — all errors are caught and logged.
 * - Times out after 5s to avoid blocking app startup.
 */
export async function checkForOtaUpdate(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  // Escape hatch for local device/simulator work. Without it every launch pulls
  // the published bundle and serves that instead of the build you just installed,
  // so native rebuilds silently appear to do nothing. Set VITE_DISABLE_OTA=false
  // (or drop it) when you specifically want to exercise the OTA path.
  if (import.meta.env.VITE_DISABLE_OTA === 'true') {
    console.log('[OTA] VITE_DISABLE_OTA is set. Skipping update check.');
    return;
  }

  try {
    // Get the currently running bundle ID (undefined = shipped APK binary)
    const { bundleId: currentBundleId } = await LiveUpdate.getCurrentBundle();

    // Fetch OTA metadata from Firestore (always from server to ensure fresh config, with timeout)
    const docRef = doc(db, OTA_DOC_PATH, OTA_DOC_ID);
    const snapshot = await withTimeout(getDocFromServer(docRef), OTA_CHECK_TIMEOUT_MS);

    if (!snapshot) {
      console.log('[OTA] OTA config check timed out. Skipping.');
      return;
    }

    if (!snapshot.exists()) {
      console.log('[OTA] No OTA config found in Firestore. Skipping.');
      return;
    }

    const metadata = snapshot.data() as OtaMetadata;

    if (!metadata.bundleId || !metadata.downloadUrl) {
      console.log('[OTA] OTA config is incomplete. Skipping.');
      return;
    }

    if (metadata.bundleId === currentBundleId) {
      console.log(`[OTA] Bundle "${currentBundleId}" is current. No update needed.`);
      return;
    }

    console.log(`[OTA] New bundle available: "${metadata.bundleId}" (current: "${currentBundleId ?? 'built-in'}").`);
    console.log(`[OTA] Downloading bundle...`);

    // Validate downloadUrl: must be HTTPS from the expected Firebase Storage host
    const expectedHost = 'firebasestorage.googleapis.com';
    try {
      const parsedUrl = new URL(metadata.downloadUrl);
      if (parsedUrl.protocol !== 'https:' || parsedUrl.hostname !== expectedHost) {
        console.error(`[OTA] Untrusted download URL (host: ${parsedUrl.hostname}). Skipping.`);
        return;
      }
    } catch {
      console.error('[OTA] Invalid downloadUrl format. Skipping.');
      return;
    }

    // Download the new bundle
    await LiveUpdate.downloadBundle({
      bundleId: metadata.bundleId,
      url: metadata.downloadUrl,
    });

    // Set it as the next bundle to load
    await LiveUpdate.setNextBundle({ bundleId: metadata.bundleId });

    console.log(`[OTA] Bundle "${metadata.bundleId}" set. Reloading...`);

    // Reload the webview with the new bundle
    await LiveUpdate.reload();
  } catch (error) {
    // Silent fail — never block app startup due to OTA errors
    console.error('[OTA] Update check failed:', error);
  }
}

/**
 * Signal that the current bundle loaded successfully.
 * Prevents auto-rollback after a successful update.
 * Call this after the React app has fully mounted.
 */
export async function confirmOtaUpdate(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await LiveUpdate.ready();
    console.log('[OTA] Bundle confirmed as ready.');
  } catch (error) {
    console.error('[OTA] Failed to confirm bundle:', error);
  }
}
